import { useCallback, useEffect, useState } from 'react';

/**
 * Density: how tall a control is and how much air a list has.
 *
 * The tokens have always said this is how it works — *"density switches the control sizes through
 * `data-density`, so nothing references density directly"* (`tokens/semantic/size.json`). This is
 * the half that sets the attribute, and `tokens.css` is the half that answers it. No component
 * reads a density value, which is the point: a control is `--input-height-md` tall in both modes,
 * and what changes is what that variable means.
 *
 * Comfortable is the default, because it is the one that meets the 44px pointer target on a touch
 * screen. Compact is for the person who watches a queue board all day on a 1366×768 laptop and
 * wants four more rows on it — a real request from a real contact centre, and the reason this is a
 * setting rather than a decision.
 */
export const densityStorageKey = 'ccp.density';

export const densities = ['comfortable', 'compact'] as const;
export type Density = (typeof densities)[number];

export function isDensity(value: unknown): value is Density {
  return typeof value === 'string' && (densities as readonly string[]).includes(value);
}

/** Reads the stored density; comfortable when nothing is stored or storage is unavailable. */
export function readStoredDensity(): Density {
  try {
    const stored = globalThis.localStorage?.getItem(densityStorageKey);
    return isDensity(stored) ? stored : 'comfortable';
  } catch {
    // A private window or blocked storage is not an error: the default applies.
    return 'comfortable';
  }
}

/**
 * Applies a density to the document.
 *
 * Comfortable removes the attribute rather than setting it, so the default lives in `:root` and
 * there is one set of values to keep in step instead of two.
 */
export function applyDensity(density: Density, root?: HTMLElement): void {
  const element = root ?? globalThis.document?.documentElement;
  if (element === undefined) {
    return;
  }
  if (density === 'comfortable') {
    element.removeAttribute('data-density');
    return;
  }
  element.setAttribute('data-density', density);
}

export interface DensityState {
  readonly density: Density;
  setDensity: (density: Density) => void;
  /** Flips between the two, which is what a single toolbar button or a palette entry does. */
  toggle: () => void;
}

/** The density preference, applied to the document and remembered for this browser. */
export function useDensity(): DensityState {
  const [density, setDensityState] = useState<Density>(readStoredDensity);

  useEffect(() => {
    applyDensity(density);
    try {
      globalThis.localStorage?.setItem(densityStorageKey, density);
    } catch {
      // Storage may be unavailable; the density still applies for this session.
    }
  }, [density]);

  const setDensity = useCallback((next: Density) => setDensityState(next), []);
  const toggle = useCallback(
    () => setDensityState((current) => (current === 'compact' ? 'comfortable' : 'compact')),
    [],
  );

  return { density, setDensity, toggle };
}
